import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache so multiple components don't refetch on the same page load.
let _cachedPromise = null;
let _cachedResult = null;

async function fetchFeatureState() {
  if (_cachedResult) return _cachedResult;
  if (_cachedPromise) return _cachedPromise;
  const token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
  if (!token) {
    // Not logged in — treat as fully gated (no admin, no override, no tier).
    _cachedResult = { features: [], membership_tier: null, grandfathered: false };
    return _cachedResult;
  }
  _cachedPromise = axios.get(`${API}/feature-gating/me`, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      _cachedResult = res.data || { features: [], membership_tier: 'starter', grandfathered: false };
      return _cachedResult;
    })
    .catch(() => {
      // Graceful degrade: if the endpoint errors, DO NOT hard-block anything.
      // Pretend the user has ambient access so the UI never crashes or blanks.
      _cachedResult = { features: ['__all__'], membership_tier: null, grandfathered: true, _degraded: true };
      return _cachedResult;
    })
    .finally(() => { _cachedPromise = null; });
  return _cachedPromise;
}

/** Test-only helper: clear the module cache. Not exported to the public API. */
export function _resetFeatureGatingCache() {
  _cachedResult = null;
  _cachedPromise = null;
}

/**
 * React hook for feature gating. Returns { canUse(feature), state, loading }.
 *
 * `canUse('__any__')` or when the backend is unreachable → returns true
 * (graceful degradation per spec: gating failure never crashes the UI).
 *
 * Grandfathered users see features list = superset of all tiers, so
 * canUse() trivially returns true for everything without special-casing.
 */
export default function useFeatureGating() {
  const [state, setState] = useState(_cachedResult);
  const [loading, setLoading] = useState(!_cachedResult);

  useEffect(() => {
    let mounted = true;
    if (_cachedResult) {
      setState(_cachedResult);
      setLoading(false);
      return;
    }
    fetchFeatureState().then(r => {
      if (mounted) { setState(r); setLoading(false); }
    });
    return () => { mounted = false; };
  }, []);

  const canUse = useCallback((feature) => {
    if (!state) return true;  // during initial load, don't flash locks
    if (state._degraded) return true;
    if (Array.isArray(state.features) && state.features.includes('__all__')) return true;
    return Array.isArray(state.features) && state.features.includes(feature);
  }, [state]);

  return {
    canUse,
    state,
    loading,
    grandfathered: Boolean(state?.grandfathered),
    tier: state?.membership_tier || 'starter',
  };
}
